import {
  formatLocalizedDate,
  getInitialLanguage,
  newsTagLabel,
} from '../../../../../shared/localization/core';

/**
 * Shared types + helpers for KF1 community news (homepage teaser, list page,
 * detail page). News is served by the platform API and proxied through the KF1
 * Vite dev proxy (`/api` → :8078).
 */

/**
 * A single news item as returned by `GET /api/news` and `GET /api/news/:id`.
 * The platform API uses `created_at` (ISO string); older/alternate payloads may
 * use `date`.
 */
export interface NewsItem {
  id: string | number;
  title: string;
  /** ISO date string from the platform API. */
  created_at?: string;
  /** Fallback date key for alternate payload shapes. */
  date?: string;
  tag?: string;
  excerpt?: string;
  /** Full update text, used for patch notes/changelog posts. */
  body?: string;
  /** Optional external "Read more" / source link. */
  link?: string | null;
  /** Pinned posts are featured on the homepage and badged on the list page. */
  pinned?: boolean;
}

/** Returns the best available date string for an item. */
export function itemDate(item: NewsItem): string {
  return item.created_at ?? item.date ?? '';
}

/** Formats an ISO date string into a short, locale-aware label. */
export function formatDate(iso: string): string {
  return formatLocalizedDate(iso, getInitialLanguage());
}

export { newsTagLabel };

/** Newest-first comparator using the item's date. */
function byNewest(a: NewsItem, b: NewsItem): number {
  return new Date(itemDate(b)).getTime() - new Date(itemDate(a)).getTime();
}

/**
 * Normalizes an unknown news payload (bare array or `{ news: [...] }`) into a
 * typed array.
 */
export function parseNewsList(data: unknown): NewsItem[] {
  if (Array.isArray(data)) return data as NewsItem[];
  const wrapped = (data as { news?: NewsItem[] })?.news;
  return Array.isArray(wrapped) ? wrapped : [];
}

/** Sorts a list pinned-first, then newest-first within each group. */
export function sortPinnedThenNewest(items: NewsItem[]): NewsItem[] {
  const pinned = items.filter((i) => i.pinned).sort(byNewest);
  const rest = items.filter((i) => !i.pinned).sort(byNewest);
  return [...pinned, ...rest];
}
